import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { CaretDownIcon as CaretDown, MagnifyingGlassIcon as MagnifyingGlass, SparkleIcon as Sparkle, XIcon as CloseIcon } from "@phosphor-icons/react";

/**
 * Breed selector — searchable combobox over the seeded breed catalog
 * (purebreds + exotic). Replaces free-text inputs everywhere a user
 * picks a breed (dog registration, litter registration, puppy rows,
 * pedigree ancestor dialog).
 *
 * Props:
 *   value         current breed name (string)
 *   onChange(s)   setter
 *   placeholder   trigger placeholder text
 *   testId        base data-testid; the trigger gets `${testId}-trigger`,
 *                 each option `${testId}-opt-<slug>`
 *   required      hint for ARIA only — caller decides validation
 */
export default function BreedPicker({
  value = "",
  onChange,
  placeholder = "Pick a breed…",
  testId = "breed-picker",
  required = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api
      .get("/breeds/catalog")
      .then((r) => setCatalog(r.data?.breeds || []))
      .catch(() => setCatalog([]));
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Auto-focus the search input when opening
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    if (!lower) return catalog;
    return catalog.filter(
      (b) =>
        b.name.toLowerCase().includes(lower) ||
        (b.origin || "").toLowerCase().includes(lower) ||
        (b.group || "").toLowerCase().includes(lower)
    );
  }, [catalog, q]);

  const grouped = useMemo(() => {
    // Show purebreds first (alphabetical), then exotics (alphabetical).
    const pure = filtered.filter((b) => !b.exotic);
    const exotic = filtered.filter((b) => b.exotic);
    return { pure, exotic };
  }, [filtered]);

  const pick = (name) => {
    onChange?.(name);
    setOpen(false);
    setQ("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-required={required}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border bg-white text-left text-sm transition-colors ${
          open ? "border-[#0E0E0E]" : "border-[#E5E5E5] hover:border-[#0E0E0E]"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid={`${testId}-trigger`}
      >
        <span className={value ? "text-[#0E0E0E] truncate" : "text-[#9A9A9A]"}>
          {value || placeholder}
        </span>
        <span className="flex items-center gap-1 ml-2 flex-shrink-0">
          {value && (
            <button
              type="button"
              onClick={clear}
              className="h-5 w-5 inline-flex items-center justify-center rounded-full text-[#6B6B6B] hover:bg-[#F0F0F0]"
              data-testid={`${testId}-clear`}
              aria-label="Clear breed"
            >
              <CloseIcon size={11} weight="bold" />
            </button>
          )}
          <CaretDown
            size={12}
            weight="bold"
            className={`text-[#6B6B6B] transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-[#E5E5E5] rounded-lg shadow-lg flex flex-col"
          style={{ maxHeight: 360 }}
          data-testid={`${testId}-popover`}
        >
          <div className="p-2 border-b border-[#E5E5E5]">
            <div className="relative">
              <MagnifyingGlass
                size={13}
                weight="duotone"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B6B6B]"
              />
              <Input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${catalog.length} breeds…`}
                className="pl-7 h-8 text-sm"
                data-testid={`${testId}-search`}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setOpen(false);
                  if (e.key === "Enter") {
                    const first = grouped.pure[0] || grouped.exotic[0];
                    if (first) pick(first.name);
                  }
                }}
              />
            </div>
          </div>
          <div className="overflow-y-auto" role="listbox">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-sm text-[#6B6B6B] text-center">
                No breeds match "{q}"
              </div>
            )}
            {grouped.pure.length > 0 && (
              <BreedSection
                label="Purebreds"
                rows={grouped.pure}
                onPick={pick}
                testId={testId}
                selected={value}
              />
            )}
            {grouped.exotic.length > 0 && (
              <BreedSection
                label="Exotic"
                rows={grouped.exotic}
                onPick={pick}
                testId={testId}
                selected={value}
                accent="#C9A227"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BreedSection({ label, rows, onPick, testId, selected, accent }) {
  return (
    <div>
      <div
        className="sticky top-0 px-3 py-1 bg-[#FAFAFA] text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] border-b border-[#E5E5E5] flex items-center gap-1"
      >
        {accent && <Sparkle size={10} weight="duotone" style={{ color: accent }} />}
        {label}
        <span className="ml-auto text-[#9A9A9A] font-normal">{rows.length}</span>
      </div>
      {rows.map((b) => {
        const isSel = b.name === selected;
        return (
          <button
            key={b.id || b.name}
            type="button"
            onClick={() => onPick(b.name)}
            role="option"
            aria-selected={isSel}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-[#FBF7EC] flex items-center justify-between gap-3 ${
              isSel ? "bg-[#FBF7EC]" : ""
            }`}
            data-testid={`${testId}-opt-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="truncate font-medium">{b.name}</span>
              {b.group && (
                <span className="text-[10px] text-[#9A9A9A] truncate">{b.group}</span>
              )}
            </span>
            {b.origin && (
              <span className="text-[10px] text-[#6B6B6B] flex-shrink-0">{b.origin}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
